import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { FormControlLabel, Switch } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";

const cleanWeightInput = value => {
  if (value == null) {
    return "";
  }

  // Weight is a whole number 1-100: strip any non-digit (decimal points, "e",
  // signs) so fractional/exponential input can't be entered, then drop leading
  // zeros.
  return value
    .toString()
    .replace(/[^\d]/g, "")
    .replace(/^0+(?=\d)/, "");
};

const isValidWeight = value => {
  if (value == null || value.toString().trim() === "") {
    return false;
  }

  const parsedValue = Number(value);

  if (!Number.isInteger(parsedValue)) {
    return false;
  }

  return parsedValue >= 1 && parsedValue <= 100;
};

const BotpressProductMarkersDialog = props => {
  const {
    open,
    markerRecord,
    attributeOptions = [],
    assignedMarkerKeys = [],
    dialogTitle = "Audience Attribute",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    deleteMessage = "Delete",
    maxWidth = "sm",
    fullWidth = true,
    onClose,
    onAccept,
    onDelete,
  } = props;

  const theme = useTheme();

  const isExisting = markerRecord?.marker != null && markerRecord.marker !== "";

  const [attribute, setAttribute] = useState(markerRecord?.marker || "");
  const [value, setValue] = useState(markerRecord?.value === true);
  const [weight, setWeight] = useState(
    markerRecord?.weight == null ? "" : cleanWeightInput(markerRecord.weight),
  );

  // Attributes already assigned to other markers should not be selectable again
  // (the attribute currently being edited stays available).
  const usedKeys = assignedMarkerKeys.filter(key => key !== markerRecord?.marker);
  const baseOptions = attributeOptions.filter(option => !usedKeys.includes(option));
  // Keep a legacy attribute value selectable even if it is not in the option list.
  const availableOptions =
    isExisting && !baseOptions.includes(markerRecord.marker)
      ? [markerRecord.marker, ...baseOptions]
      : baseOptions;

  const attributeHasError = attribute == null || attribute === "";
  const weightHasError = !isValidWeight(weight);
  const formHasError = attributeHasError || weightHasError;

  const onAcceptHandler = () => {
    if (formHasError) {
      return;
    }

    onAccept({
      marker: attribute,
      value: value,
      weight: Number(weight),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onClose}>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Grid spacing={1} container size={{ xs: 12 }}>
          <Grid item size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Attribute"}
              select
              required
              variant="outlined"
              name={uuid()}
              id={"markerAttribute"}
              value={attribute}
              error={attributeHasError}
              helperText={attributeHasError ? "Attribute is required." : ""}
              onChange={event => setAttribute(event.target.value)}
            >
              {availableOptions.map(option => (
                <OmniMenuItem key={option} value={option}>
                  {option}
                </OmniMenuItem>
              ))}
            </OmniTextField>
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Weight"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"markerWeight"}
              value={weight}
              error={weightHasError}
              helperText={weightHasError ? "Weight must be 1-100." : ""}
              onChange={event => setWeight(cleanWeightInput(event.target.value))}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={value}
                  onChange={event => setValue(event.target.checked)}
                  color="secondary"
                />
              }
              label={value ? "True" : "False"}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {isExisting && onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => onDelete(markerRecord)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onClose}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button
            onClick={onAcceptHandler}
            color="secondary"
            variant="contained"
            disabled={formHasError}
          >
            {okMessage}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BotpressProductMarkersDialog;
